/**
 * اعلانکِ داخلِ صفحه — «وقتی پنجره باز است هم چیزی ببینم» (WHOSUP-71).
 *
 * ═══ چرا از کانالِ زنده و نه از پیامِ فایربیس ═══
 *
 * وقتی تبِ سایت جلوی چشم است، مرورگر عمداً اعلانِ سیستمی نشان نمی‌دهد و
 * پیامِ پوش به خودِ صفحه تحویل می‌شود. ولی صفحه راهِ تندتری از قبل دارد:
 * همان کانالِ Realtime که شمارنده‌ها را تازه می‌کند (لایه‌ی ۱ در
 * useNotifIndicators)، سطرِ کامل را همان لحظه‌ی درج می‌دهد — بدونِ لود
 * کردنِ SDK فایربیس، و برای همه، حتی کسی که پوشِ مرورگرش را روشن نکرده.
 *
 * ⚠ کانالِ زنده یکی به‌ازای هر کاربر است (`notif-${uid}`؛ اشتراکِ دوم
 * اولی را می‌کُشد)، پس این‌جا مشترکِ تازه‌ای ساخته نمی‌شود:
 * `useNotifIndicators` — تنها مشترک — سطرِ تازه را در `emitFreshNotification`
 * می‌ریزد و این کامپوننت فقط شنونده‌ی همان است.
 *
 * متن از کلیدهای ترجمه ساخته می‌شود (`notifText`)، نه از ستونِ `title` —
 * همان قاعده‌ی «هرجا خودِ اپ نشان می‌دهد» در domain/types.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { AppNotification } from '@/core/domain/types';
import { categoryOfKind, isAlarm } from '@/core/data/miscRepo';
import { DEFAULT_PREFS, notifPrefsRepo } from '@/core/data/notifPrefsRepo';
import { notifBody, notifTitle } from '@/core/lib/notifText';
import { notifWebTarget } from '@/core/lib/notifTarget';
import { t, useLocale } from '@/core/lib/i18n';
import { useSession } from '@/core/session';

type Listener = (n: AppNotification) => void;
const listeners = new Set<Listener>();

/** تنها مشترکِ کانالِ زنده (useNotifIndicators) سطرِ تازه را این‌جا می‌ریزد. */
export function emitFreshNotification(n: AppNotification): void {
  for (const fn of listeners) fn(n);
}

/** آن‌قدر که بشود خواند، نه آن‌قدر که مزاحم شود. */
const SHOW_MS = 8000;

export function ForegroundNotices() {
  useLocale();
  const nav = useNavigate();
  const { profile } = useSession();
  const [items, setItems] = React.useState<AppNotification[]>([]);

  // همان کوئریِ کش‌شده‌ی useNotifIndicators — کلیدِ مشترک یعنی درخواستِ اضافه نه.
  const prefs = useQuery({
    queryKey: ['notifPrefs'],
    queryFn: notifPrefsRepo.mine,
    enabled: !!profile,
  });
  const prefsRef = React.useRef(DEFAULT_PREFS);
  prefsRef.current = prefs.data ?? DEFAULT_PREFS;

  const dismiss = (id: number) => setItems((xs) => xs.filter((x) => x.id !== id));

  React.useEffect(() => {
    const on: Listener = (n) => {
      // تبِ پنهان اعلانک لازم ندارد — آن‌جا اعلانِ سیستمیِ پوش کارش را
      // می‌کند، و کاربری که ساعتی بعد برگردد نباید با پیام‌های بیات روبه‌رو شود.
      if (document.visibilityState !== 'visible') return;
      // دسته‌ای که کاربر خاموشش کرده، این‌جا هم ساکت است — جز «الارم»
      // (مایگریشن ۰۰۷۲) که عمداً از تنظیم رد می‌شود.
      if (!isAlarm(n) && prefsRef.current[categoryOfKind(n.kind)] === 'off') return;
      setItems((xs) => [...xs.filter((x) => x.id !== n.id), n].slice(-3));
      window.setTimeout(() => dismiss(n.id), SHOW_MS);
    };
    listeners.add(on);
    return () => { listeners.delete(on); };
  }, []);

  if (!items.length) return null;

  return (
    <div className="notices" role="status" aria-live="polite">
      {items.map((n) => {
        const body = notifBody(n);
        // `null` یعنی سطری بدونِ مقصد (نگاه کن به notifTarget) — اعلانک
        // بسته می‌شود و جایی پرت نمی‌کند.
        const open = () => {
          dismiss(n.id);
          const to = notifWebTarget(n.data);
          if (to) nav(to);
        };
        return (
          /* متنِ اعلان داده است (اسمِ آدم/برنامه)، پس `data-tap` تا لاگِ
             لمس‌ها (boot.ts) برچسبِ ثابت بردارد نه متن را. */
          <div key={n.id} className="notice" role="button" tabIndex={0}
            data-tap="foregroundNotice"
            onClick={open}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } }}>
            <div className="notice-text">
              <strong>{notifTitle(n)}</strong>
              {body ? <span className="dim">{body}</span> : null}
            </div>
            <button type="button" className="notice-x" aria-label={t('filterClose')}
              onClick={(e) => { e.stopPropagation(); dismiss(n.id); }}>
              ✕
            </button>
          </div>
        );
      })}
    </div>
  );
}
